import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Snowflake, Euro, Calendar, Edit2, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

const ChristmasWorks = ({ user }) => {
  const [works, setWorks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    startDate: '',
    endDate: '',
    description: '',
    cost: 0,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [worksRes, statsRes] = await Promise.all([
        axiosInstance.get('/christmas-works'),
        axiosInstance.get('/christmas-works/stats')
      ]);
      setWorks(worksRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (workId, e) => {
    const files = Array.from(e.target.files);
    setUploading(true);

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axiosInstance.post(`/christmas-works/${workId}/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data.photo;
      });

      await Promise.all(uploadPromises);
      toast.success('Foto caricate!');
      fetchData();
    } catch (error) {
      toast.error('Errore durante il caricamento delle foto');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axiosInstance.put(`/christmas-works/${editingId}`, formData);
        toast.success('Lavori aggiornati!');
      } else {
        await axiosInstance.post('/christmas-works', formData);
        toast.success('Lavori registrati!');
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questi lavori?')) return;
    try {
      await axiosInstance.delete(`/christmas-works/${id}`);
      toast.success('Lavori eliminati!');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const openEditDialog = (work) => {
    setFormData({
      year: work.year,
      startDate: work.startDate,
      endDate: work.endDate,
      description: work.description,
      cost: work.cost,
      notes: work.notes || ''
    });
    setEditingId(work.id);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      year: new Date().getFullYear(),
      startDate: '',
      endDate: '',
      description: '',
      cost: 0,
      notes: ''
    });
    setEditingId(null);
  };

  const canEdit = user?.role === 'admin' || user?.role === 'maintainer';
  const canDelete = user?.role === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="christmas-works-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Lavori Natale</h1>
          <p className="text-gray-600">Gestione lavori periodo natalizio per anno</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetForm}
                className="bg-gradient-to-r from-red-500 to-green-600 hover:from-red-600 hover:to-green-700"
                data-testid="add-christmas-work-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovi Lavori
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="christmas-work-dialog">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Modifica Lavori Natale' : 'Nuovi Lavori Natale'}</DialogTitle>
                <DialogDescription>
                  Registra i lavori effettuati durante il periodo natalizio
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="year">Anno</Label>
                  <Input
                    id="year"
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    required
                    data-testid="christmas-year-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startDate">Data Inizio</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      required
                      data-testid="christmas-start-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="endDate">Data Fine</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      required
                      data-testid="christmas-end-date-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">Descrizione Lavori</Label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                    required
                    data-testid="christmas-description-textarea"
                  />
                </div>
                <div>
                  <Label htmlFor="cost">Costo (€)</Label>
                  <Input
                    id="cost"
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })}
                    required
                    data-testid="christmas-cost-input"
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Note</Label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                    data-testid="christmas-notes-textarea"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-red-500 to-green-600 hover:from-red-600 hover:to-green-700"
                  data-testid="christmas-submit-button"
                >
                  {editingId ? 'Aggiorna Lavori' : 'Registra Lavori'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats Cards */}
      {stats && stats.totalWorks > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="shadow-lg border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <Snowflake className="w-4 h-4 mr-2" />
                Lavori Totali
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-red-600">{stats.totalWorks}</p>
              <p className="text-xs text-gray-500 mt-1">Registrati</p>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <Euro className="w-4 h-4 mr-2" />
                Costo Totale
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">€{stats.totalCost.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">Storico</p>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                Costo Medio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600">€{stats.averageCost.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">Per anno</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Works List */}
      <div className="space-y-6">
        {works.length === 0 ? (
          <Card className="shadow-lg border-0">
            <CardContent className="py-12">
              <div className="text-center">
                <Snowflake className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500" data-testid="no-works">Nessun lavoro natalizio registrato</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          works.map((work) => (
            <Card key={work.id} className="card-hover shadow-lg border-0 overflow-hidden" data-testid={`work-card-${work.id}`}>
              <div className="h-2 bg-gradient-to-r from-red-500 to-green-600"></div>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center flex-1">
                    <div className="p-3 rounded-lg bg-gradient-to-br from-red-500 to-green-600 mr-4">
                      <Snowflake className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl mb-2">Lavori Natale {work.year}</CardTitle>
                      <div className="flex gap-4 text-sm text-gray-600 mb-2">
                        <span>Dal {new Date(work.startDate).toLocaleDateString('it-IT')}</span>
                        <span>al {new Date(work.endDate).toLocaleDateString('it-IT')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Euro className="w-4 h-4 text-green-600" />
                        <span className="text-xl font-bold text-green-600">€{work.cost.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditDialog(work)}
                        className="p-2 rounded-lg hover:bg-blue-50"
                        data-testid={`edit-work-${work.id}`}
                      >
                        <Edit2 className="w-4 h-4 text-blue-600" />
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(work.id)}
                          className="p-2 rounded-lg hover:bg-red-50"
                          data-testid={`delete-work-${work.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Descrizione Lavori</h4>
                    <p className="text-gray-700 whitespace-pre-line">{work.description}</p>
                  </div>
                  
                  {work.notes && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Note</h4>
                      <p className="text-gray-600 text-sm">{work.notes}</p>
                    </div>
                  )}

                  {/* Photos Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">Foto</h4>
                      {canEdit && (
                        <label className="cursor-pointer">
                          <div className="flex items-center px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors">
                            <Upload className="w-4 h-4 mr-2 text-blue-600" />
                            <span className="text-sm text-blue-600">
                              {uploading ? 'Caricamento...' : 'Carica foto'}
                            </span>
                          </div>
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => handlePhotoUpload(work.id, e)}
                            className="hidden"
                            disabled={uploading}
                          />
                        </label>
                      )}
                    </div>
                    {work.photos && work.photos.length > 0 ? (
                      <div className="grid grid-cols-4 gap-3">
                        {work.photos.map((photo, index) => (
                          <img
                            key={index}
                            src={photo}
                            alt={`Foto ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => window.open(photo, '_blank')}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">Nessuna foto disponibile</p>
                    )}
                  </div>

                  <div className="pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      Registrato da {work.recordedBy} il {new Date(work.createdAt).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ChristmasWorks;
